import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Minus, Plus } from "lucide-react";

const MenuGallery = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });

  // Replace these with your actual Cloudinary menu image URLs
  const menuImages = [
    {
      id: 1,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907455/menu_1_ogkzc8.jpg",
      title: "Main Menu",
    },
    {
      id: 2,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907454/menu_2_ufmer5.jpg",
      title: "Beverage Menu",
    },
    {
      id: 3,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907454/menu_3_xk6jii.jpg",
      title: "Dessert Menu",
    },
    {
      id: 4,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907454/menu_4_x3hamc.jpg",
      title: "Breakfast Menu",
    },
    {
      id: 5,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907455/menu_5_tkr1fa.jpg",
      title: "Lunch Specials",
    },
    {
      id: 6,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907455/menu_6_jp804t.jpg",
      title: "Dinner Menu",
    },
    {
      id: 7,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907454/menu_7_uqn2st.jpg",
      title: "Wine List",
    },
    {
      id: 8,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907455/menu_8_eguodr.jpg",
      title: "Cocktail Menu",
    },
    {
      id: 9,
      url: "https://res.cloudinary.com/dxdvcplbl/image/upload/v1763907454/menu_9_dnldnr.jpg",
      title: "Seasonal Specials",
    },
  ];

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - startPosition.x;
    const newY = e.clientY - startPosition.y;

    // Calculate bounds based on zoom level
    const maxMove = (zoomLevel - 1) * 100;

    setPosition({
      x: Math.max(Math.min(newX, maxMove), -maxMove),
      y: Math.max(Math.min(newY, maxMove), -maxMove),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setSelectedImage(null);
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
      }
    };

    if (selectedImage) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [selectedImage]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-lime-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 mb-6 transition-colors"
          >
            <ChevronLeft size={20} />
            Back to Restaurant
          </button>

          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4 tracking-tight">
            Our Menu Collection
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto">
            Explore our complete range of culinary offerings
          </p>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {menuImages.map((menu) => (
            <div
              key={menu.id}
              className="group bg-white rounded-[2px] shadow-2xl hover:shadow-3xl transition-all duration-300 overflow-hidden cursor-pointer transform hover:scale-[1.02] flex flex-col"
              onClick={() => setSelectedImage(menu)}
            >
              {/* Image Container with proper object-contain handling */}
              <div className="relative flex-1 flex items-center justify-center bg-gray-50 p-4 min-h-[280px]">
                <img
                  src={menu.url}
                  alt={menu.title}
                  className="w-full h-full object-contain max-w-full max-h-[240px] group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4 bg-white/90 text-green-600 px-3 py-1 rounded-full text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  View
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-12 bg-white rounded-3xl shadow-2xl p-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
            Need Help Choosing?
          </h2>
          <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
            Our staff is always happy to help you navigate our menu and make
            recommendations based on your preferences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-green-500 to-lime-500 text-white font-semibold py-3 px-8 rounded-2xl hover:shadow-xl transition-all duration-300">
              Call: +88 01769660123
            </button>
            <button
              onClick={() => navigate(-1)}
              className="border-2 border-green-500 text-green-600 font-semibold py-3 px-8 rounded-2xl hover:bg-green-50 transition-all duration-300"
            >
              Back to Restaurant
            </button>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setSelectedImage(null);
            setZoomLevel(1);
            setPosition({ x: 0, y: 0 });
          }}
        >
          <div
            className="relative w-full h-full max-w-6xl max-h-[90vh] bg-white rounded-3xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 bg-white border-b shrink-0">
              {/* <h3 className="text-lg font-semibold text-gray-900">
                {selectedImage.title}
              </h3> */}

              <div className="flex items-center gap-3">
                {/* Zoom Controls */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                    className="p-2 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Zoom Out"
                  >
                    <Minus size={16} />
                  </button>

                  <button
                    onClick={resetZoom}
                    className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded transition-colors min-w-[60px]"
                    title="Reset Zoom"
                  >
                    {Math.round(zoomLevel * 100)}%
                  </button>

                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 5}
                    className="p-2 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Zoom In"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => {
                    setSelectedImage(null);
                    setZoomLevel(1);
                    setPosition({ x: 0, y: 0 });
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Close (ESC)"
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
            </div>

            {/* Image Container */}
            <div
              className="flex-1 overflow-hidden relative"
              onWheel={handleWheel}
            >
              <div
                className="w-full h-full flex items-center justify-center cursor-move"
                style={{
                  cursor:
                    zoomLevel > 1
                      ? isDragging
                        ? "grabbing"
                        : "grab"
                      : "default",
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  src={selectedImage.url}
                  alt={selectedImage.title}
                  className="max-w-full max-h-full object-contain transition-transform duration-200 select-none"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                    transition: isDragging ? "none" : "transform 0.2s ease",
                  }}
                  draggable="false"
                />
              </div>

              {/* Zoom Instructions */}
              {zoomLevel === 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/75 text-white px-4 py-2 rounded-lg text-sm pointer-events-none">
                  Scroll to zoom • Click and drag to pan when zoomed
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuGallery;
